<template>
  <article>
    <p>
      Across the wine world, different regions are known for producing different
      grapes and subsequently different styles of wine.
    </p>
    <p>
      The distinction between Old World wine regions and New World wine regions
      is particularly important: as a sweeping generalization, Old World regions
      are known for producing wines that are more austere and terroir-driven,
      while New World regions are known for producing wines with greater
      intensity. For example, Cabernet Sauvignon grapes grown in France will
      likely produce wines marked by earthiness and subtle fruit flavors, while
      Cabernet Sauvignon grapes grown in California will likely produce wines
      that are more fruit-forward.
    </p>
    <p>
      But different wine regions are also often associated with the production
      of different grapes. For example, within France, Bordeaux is associated
      with red wines made primarily from Cabernet Sauvignon, Merlot, and
      Cabernet Franc grapes, while Burgundy is associated with red wines made
      from the Pinot Noir grape.
    </p>
    <p>
      There are many notable wine regions around the world. Take the Loire
      Valley, a well known wine region in central France that spans 300 miles.
      In Spain, you can find the La Rioja wine region, an autonomous community
      located in the north of the Iberian Peninsula. In New Zealand, there is
      the Waitaki Valley, a small wine region in North Otago on the east coast
      of the South Island. There is also Central Otago, the world's
      southern-most commercial wine growing region. Also in New Zealand you can
      find Hawkes Bay which not only is the second largest wine region in New
      Zealand but also the oldest. Not too far away is Yarra Valley in
      Australia, just 90 kilometers east of Melbourne. You can also find Mclaren
      Vale, Australia's premium food and wine region located in the town of
      McLaren. Zooming over to North America, you can find the Paso Robles wine
      region located halfway between Los Angeles and San Francisco. In Northern
      California, Sonoma County houses well over 400 wineries. Then there is
      Willamette Valley, home to two-thirds of Oregon's wineries and vineyards.
      There is also Mendoza Argentinas, considered to be Argentina's most
      important wine region.
    </p>
    <p>
      The list of major wine regions only goes on from there. There is the Douro
      Valley wine producing region in Northern Portugal. There are also famous
      wine regions in Santa Barbara, North Canterbury, British Columbia (where
      you can find Okanagan Valley), South Australia, Cape Town, and Russian
      River Valley.
    </p>
    <p>
      Wine production is big business, and there are plenty of wine growing
      regions around the world to meet global and local demand. On a side note,
      Napa Valley is recommended if you're looking for some of the best wine
      tours in the United States!
    </p>
    <p>
      Use this page to find top-rated wines from wine regions throughout the
      world and learn about each region's wine-making profile.
    </p>
    <p>
      Across the wine world, different regions are known for both producing
      different grapes and different styles of wine.
    </p>

    <p>
      The distinction between Old World wine regions and New World wine regions
      is particularly important: as a sweeping generalization, Old World regions
      are known for producing wines that are more austere and terroir driven,
      while New World regions are known for producing wines with greater
      intensity. For example, Cabernet Sauvignon grapes grown in France will
      likely produce wines marked by earthiness and subtle fruit flavors, while
      Cabernet Sauvignon grapes grown in California will likely produce wines
      that are more fruit-forward.
    </p>

    <p>
      But different wine regions are also often associated with the production
      of different grapes. For example, within France, Bordeaux is associated
      with red wines made primarily from Cabernet Sauvignon, Merlot, and
      Cabernet Franc grapes, while Burgundy is associated with red wines made
      from the Pinot Noir grape. Use this page to find top-rated wines from wine
      regions throughout the world and learn about each region’s wine-making
      profile.
    </p>

    <div class="regions">
      <ul class="multilevel nobullet">
        <li
          v-for="[continent, countries] of Object.entries(continents)"
          :key="continent"
        >
          <h2>{{ continent }}</h2>
          <ul v-if="countries">
            <li
              v-for="[countryName, country] of Object.entries(countries)"
              :key="country.slug"
            >
              <nuxt-link :to="`/store/regions/${country.slug}`">{{
                countryName
              }}</nuxt-link>
              <ul v-if="country.regions">
                <li
                  v-for="[regionName, region] of Object.entries(
                    country.regions,
                  )"
                  :key="region.slug"
                >
                  <nuxt-link :to="`/store/regions/${region.slug}`">{{
                    regionName
                  }}</nuxt-link>
                  <ul
                    v-if="region.subregions"
                    itemscope
                    itemtype="http://schema.org/ItemList"
                  >
                    <meta
                      itemprop="name"
                      :content="`Wine regions of ${region.name}`"
                    />
                    <link
                      itemprop="itemListOrder"
                      href="http://schema.org/ItemListOrderAscending"
                    />
                    <li
                      v-for="[subregionName, subregion] of Object.entries(
                        region.subregions,
                      )"
                      :key="subregion.slug"
                    >
                      <nuxt-link
                        itemprop="itemListElement"
                        :to="`/store/regions/${subregion.slug}`"
                        >{{ subregionName }}</nuxt-link
                      >
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </article>
</template>

<script>
export default {
  data() {
    return {
      continents: {},
    }
  },
  async fetch() {
    const result = await this.$axios.get('/api/learn/regions')
    this.continents = result.data.continents
  },
  head: {
    meta: [
      {
        hid: 'description',
        name: 'description',
        content:
          'Across the wine world, different regions are known for producing different grapes and subsequently different styles of wine.',
      },
    ],
  },
}
</script>

<style scoped lang="scss">
div.regions {
  columns: 3;
  column-gap: 30px;
  column-rule: 1px solid #c9c9c9;
  max-width: 1000px;
  margin: 0 auto;

  ul li {
    font-size: 1.5rem;

    li {
      font-size: 1.375rem;

      li {
        font-size: 1.25rem;

        li {
          font-size: 1.125rem;
        }
      }
    }
  }
}
</style>
